/**
 * 問題データのスキーマ検証。
 *
 * 問題は**手書きの JSON**(`tools/content-build/data/curated/quiz-*.json`)なので、
 * 「正解が2つある」「選択肢が1つしかない」といった書き間違いは人間の目では見落とす。
 * ここを content-build が **ビルドエラー**として使い、壊れた問題が配信されるのを止める
 * (カタログ・curated 単語と同じ思想)。
 *
 * 返り値は**エラー文字列の配列**(空なら OK)。例外を投げないのは、
 * 1回のビルドで全部の問題の不備をまとめて出したいため。
 */

import { QUIZ_CHOICES, QUIZ_STAGE, QUIZ_UNIT } from './config.js';
import { validateQuizLesson } from './lesson.js';
import { quizStageId } from './stages.js';
import {
  QUIZ_ASSET_PATH,
  QUIZ_CONTROL_CHARS,
  QUIZ_ID_PATTERN,
  QUIZ_KANJI,
  QUIZ_RUBY,
  QUIZ_RUBY_SEGMENT,
  isQuizAssetPath,
  isRecord,
  isText,
} from './text.js';
import {
  QUIZ_BANDS,
  QUIZ_PRESENTATIONS,
  QUIZ_QUESTION_TYPES,
  QUIZ_SCHEMA_VERSION,
  QUIZ_SUBJECTS,
  QUIZ_SUBJECTS_BY_BAND,
  allowsKanjiChoice,
  allowsKanjiPrompt,
  type QuizBand,
  type QuizQuestion,
  type QuizSubject,
} from './types.js';

// 文字づかいの決めごとは text.ts(解説パートと共有する下の層)。
// 公開名はここから出ていたので、外から見える形を変えないよう再輸出する。
export {
  QUIZ_ASSET_PATH,
  QUIZ_ID_PATTERN,
  QUIZ_KANJI,
  QUIZ_RUBY,
  QUIZ_RUBY_SEGMENT,
  isQuizAssetPath,
};

/**
 * 既知の帯なら `QuizBand` として返す(未知の値は `undefined`)。
 * 呼び先は `undefined` を「いちばん厳しい帯」として扱う(まだ漢字を読めない子と同じ)。
 */
function knownBandOf(value: unknown): QuizBand | undefined {
  return (QUIZ_BANDS as readonly string[]).includes(value as string)
    ? (value as QuizBand)
    : undefined;
}

/** アセットの相対パス(省略可)。空文字・絶対パス・親ディレクトリ参照を弾く。 */
function checkOptionalPath(value: unknown, where: string, field: string, errors: string[]): void {
  if (value === undefined) return;
  if (!isText(value)) {
    errors.push(`${where}: ${field} が空です`);
    return;
  }
  if (!isQuizAssetPath(value)) {
    errors.push(
      `${where}: ${field} は assets/ からの相対パスで書いてください` +
        `(例 img/clock/clock-3-30.webp): ${value}`,
    );
  }
}

/**
 * 画像の代替テキスト(省略可)。
 *
 * **答えを書かせない**のがここの仕事。「とけい」のような かなの汎用語だけを許し、
 * 数字・漢字を含む説明的な alt(「3じ30ぷん」「三時半」)を弾く。
 * 読み上げソフトは alt をそのまま読むので、ここが緩いと答えが音で漏れる。
 */
function checkOptionalImageAlt(
  value: unknown,
  image: unknown,
  where: string,
  field: string,
  errors: string[],
): void {
  if (value === undefined) return;
  if (typeof value !== 'string') {
    errors.push(`${where}: ${field} は文字列が必要です`);
    return;
  }
  if (image === undefined) {
    errors.push(`${where}: ${field} は image と いっしょに つかいます`);
    return;
  }
  if (value !== '' && !/^[ぁ-んァ-ヶー ]+$/u.test(value)) {
    errors.push(
      `${where}: ${field} は かなの ふつうの ことば(例「とけい」)だけにしてください` +
        `(こたえが もれます): ${value}`,
    );
  }
}

/**
 * 絵文字のステッカー画像(`emojiImages`)を検証する。
 *
 * 中身はビルド時(`tools/content-build/src/quiz-emoji.ts`)に機械で作るので、
 * ここで見るのは「壊れたまま配信に載る」ことを止めるぶんだけ:
 *   - `emoji`(絵文字の原文)と いっしょにしか存在しない —— **原文はフォールバック**で、
 *     画像が配備されていない環境・読み込み失敗のときはそちらに戻る
 *   - 画像は `assets/` からの相対パス、`count` は1以上の整数
 *   - つなぎことば(`text`)は空でない文字列で、**画像が1つも無い列は作らない**
 *     (絵文字が1つも画像にならなかったのなら、そもそも足さないのが正しい)
 */
function checkEmojiImages(
  value: unknown,
  emoji: unknown,
  where: string,
  field: string,
  errors: string[],
): void {
  if (value === undefined) return;
  if (!Array.isArray(value) || value.length === 0) {
    errors.push(`${where}: ${field} は空でない配列が必要です`);
    return;
  }
  if (!isText(emoji)) {
    errors.push(`${where}: ${field} は emoji(絵文字の原文)と いっしょに つかいます`);
    return;
  }

  let images = 0;
  for (const [i, part] of value.entries()) {
    const at = `${where}: ${field}[${i}]`;
    if (!isRecord(part)) {
      errors.push(`${at}: オブジェクトではありません`);
      return;
    }
    const { file, count, text } = part;
    if (file !== undefined) {
      if (text !== undefined) {
        errors.push(`${at}: file と text は いっしょに つかえません`);
        return;
      }
      images += 1;
      checkOptionalPath(file, where, `${field}[${i}].file`, errors);
      if (typeof count !== 'number' || !Number.isInteger(count) || count < 1) {
        errors.push(`${at}: count は1以上の整数が必要です`);
      }
    } else if (text !== undefined) {
      if (!isText(text)) errors.push(`${at}: text が空です`);
      else if (count !== undefined) errors.push(`${at}: つなぎことばに count は付けません`);
    } else {
      errors.push(`${at}: file か text のどちらかが必要です`);
    }
  }

  if (images === 0) {
    errors.push(`${where}: ${field} に画像が1つもありません(絵文字のままにしてください)`);
  }
}

/**
 * **漢字版**フィールド(`textKanji` / `explanationKanji`)を検証する。
 *
 * 漢字版は「もじの ひょうじ = 漢字」を選んだ子にだけ出す控えなので、
 * かな版と違って中身の漢字は制限しない(**ふりがなが必ず付く**ので学年配当の
 * 上限も見ない。ユーザー指示 2026-08-25)。ここで見るのは**壊れていないこと**だけ:
 *   - 空でない・前後に空白が無い・制御文字が混ざっていない
 *   - かな版が無いのに漢字版だけある(片側だけの控え)ことが無い
 *   - かな版と同じ文字列ではない(足しても意味の無い控えを配信に載せない)
 *
 * 帯は見ない —— kinder / e1 も漢字版を持てる(かな版の `text` は かなのままで、
 * 漢字版は ふりがな付きの控えとして横に付くだけ)。
 */
function checkKanjiVariant(
  value: unknown,
  kana: unknown,
  where: string,
  field: string,
  errors: string[],
): void {
  if (value === undefined) return;
  if (!isText(value)) {
    errors.push(`${where}: ${field} が空です`);
    return;
  }
  if (!isText(kana)) {
    errors.push(`${where}: ${field} は かな版と いっしょに つかいます`);
    return;
  }
  if (value !== value.trim()) errors.push(`${where}: ${field} の前後に空白があります`);
  if (QUIZ_CONTROL_CHARS.test(value)) {
    errors.push(`${where}: ${field} に制御文字が入っています`);
  }
  if (value === kana) {
    errors.push(`${where}: ${field} が かな版と同じです(漢字版なら付けないでください)`);
  }
}

/**
 * ふりがなの**区切り**(`textSegments` / `textKanjiSegments`)を検証する。
 *
 * 区切りは画面の見せ方を変えるだけの控えなので、**壊れていたら足さない**のが正しい
 * (生成側はフォールバックして文まるごとの ruby に戻す)。ここでは
 * 「壊れたまま配信に載る」ことだけを止める:
 *   - つなげると本文と1文字たがわず一致する(勝手に足し引きしていない)
 *   - ふりがな付きの区切りは**漢字を含む**(かなの上にふりがなを乗せない = 今回の主題)
 *   - ふりがなの無い区切りは**漢字を含まない**(読めない漢字を裸で出さない)
 *   - ふりがなは かな だけ
 *
 * @param owner その区切りが切り分けている本文(`text` / `textKanji`)
 */
function checkTextSegments(
  value: unknown,
  owner: unknown,
  where: string,
  field: string,
  errors: string[],
): void {
  if (value === undefined) return;
  if (!Array.isArray(value) || value.length === 0) {
    errors.push(`${where}: ${field} は空でない配列が必要です`);
    return;
  }
  if (!isText(owner)) {
    errors.push(`${where}: ${field} は本文と いっしょに つかいます`);
    return;
  }

  let joined = '';
  for (const [i, segment] of value.entries()) {
    const at = `${where}: ${field}[${i}]`;
    if (!isRecord(segment)) {
      errors.push(`${at}: オブジェクトではありません`);
      return;
    }
    const { t, r } = segment;
    if (typeof t !== 'string' || t === '') {
      errors.push(`${at}: t が空です`);
      return;
    }
    joined += t;
    if (r === undefined) {
      if (QUIZ_KANJI.test(t)) {
        errors.push(`${at}: 漢字を含むのに ふりがな(r)がありません: ${t}`);
      }
      continue;
    }
    if (typeof r !== 'string' || !QUIZ_RUBY_SEGMENT.test(r)) {
      errors.push(`${at}: r は かな だけで書いてください: ${String(r)}`);
      continue;
    }
    // ふりがなは**漢字のため**にある。かなの上に乗せるのがこの機能で直したかった不具合。
    if (!QUIZ_KANJI.test(t)) {
      errors.push(`${at}: 漢字を含まない部分に ふりがな(r)は付けません: ${t}`);
    }
  }

  if (joined !== owner) {
    errors.push(`${where}: ${field} をつなぐと本文と一致しません: ${joined} ≠ ${owner}`);
  }
}

/**
 * 問題文を検証する。
 *
 * 漢字の可否は**帯によって変わる**(`allowsKanjiPrompt`):
 *   - kinder / e1 … 漢字は書けない(まだ読めない。ふりがなを振っても出さない)
 *   - e2 以降 …… 漢字を書いてよいが `ruby`(ふりがな)が必須
 * 未知の帯は「まだ読めない子」と同じ扱い(いちばん厳しい方)にする。
 *
 * 漢字の読み方を問う問題は `rubyHidden` で ruby の表示だけを止める
 * (ふりがなに答えが出てしまうため。ruby は読み上げ用の読みとして残す)。
 */
function validatePrompt(
  value: unknown,
  type: unknown,
  band: unknown,
  where: string,
  errors: string[],
): void {
  if (!isRecord(value)) {
    errors.push(`${where}: prompt はオブジェクトが必要です`);
    return;
  }
  const {
    text,
    textKanji,
    textSegments,
    textKanjiSegments,
    ruby,
    rubyHidden,
    emoji,
    emojiImages,
    image,
    imageAlt,
    audio,
  } = value;
  const kanjiOk =
    (QUIZ_BANDS as readonly string[]).includes(band as string) &&
    allowsKanjiPrompt(band as QuizBand);

  if (!isText(text)) {
    errors.push(`${where}: prompt.text が空です`);
  } else if (text !== text.trim()) {
    errors.push(`${where}: prompt.text の前後に空白があります`);
  } else if (QUIZ_KANJI.test(text)) {
    if (!kanjiOk) {
      errors.push(
        `${where}: ${String(band)} の prompt.text に漢字は使えません` +
          `(ひらがな・カタカナで書いてください): ${text}`,
      );
    } else if (ruby === undefined) {
      errors.push(`${where}: prompt.text に漢字があるので ruby(ふりがな)が必要です`);
    }
  }

  if (ruby !== undefined) {
    if (!isText(ruby)) errors.push(`${where}: prompt.ruby が空です`);
    else if (!QUIZ_RUBY.test(ruby)) {
      errors.push(`${where}: prompt.ruby はかな(と数字・約物)だけで書いてください: ${ruby}`);
    }
  }

  // 漢字版の問題文(docs/13。「もじの ひょうじ = 漢字」を選んだ子にだけ出す控え)。
  checkKanjiVariant(textKanji, text, where, 'prompt.textKanji', errors);
  // **読みを問う問題には漢字版を付けない**。漢字版を出した時点で答えが漏れる。
  if (rubyHidden === true && textKanji !== undefined) {
    errors.push(`${where}: rubyHidden の問題に prompt.textKanji(漢字版)は付けられません`);
  }

  // ふりがなの区切り(docs/13 §12)。漢字の部分にだけ ruby を掛けるための控え。
  checkTextSegments(textSegments, text, where, 'prompt.textSegments', errors);
  checkTextSegments(textKanjiSegments, textKanji, where, 'prompt.textKanjiSegments', errors);
  // 読みを問う問題は ふりがな そのものを出さないので、区切りも持たない
  // (区切りを持てば漢字1字ごとの読みが並び、答えがそのまま見える)。
  if (rubyHidden === true && textSegments !== undefined) {
    errors.push(`${where}: rubyHidden の問題に prompt.textSegments は付けられません`);
  }
  // 区切りは ふりがな の見せ方なので、ふりがなが無い問題には意味が無い。
  if (textSegments !== undefined && ruby === undefined) {
    errors.push(`${where}: prompt.textSegments は ruby と いっしょに つかいます`);
  }

  // 表示を止めるふりがなは「読み上げ用の読み」。ふりがなが無ければ止める物が無い。
  if (rubyHidden !== undefined) {
    if (typeof rubyHidden !== 'boolean') {
      errors.push(`${where}: prompt.rubyHidden は true/false が必要です`);
    } else if (rubyHidden && ruby === undefined) {
      errors.push(`${where}: prompt.rubyHidden は ruby(よみあげ用のよみ)と いっしょに つかいます`);
    }
  }

  // emoji は絵文字そのもの(パスではない)ので、空でないことだけを見る。
  if (emoji !== undefined && !isText(emoji)) errors.push(`${where}: prompt.emoji が空です`);
  checkEmojiImages(emojiImages, emoji, where, 'prompt.emojiImages', errors);
  checkOptionalPath(image, where, 'prompt.image', errors);
  checkOptionalImageAlt(imageAlt, image, where, 'prompt.imageAlt', errors);
  checkOptionalPath(audio, where, 'prompt.audio', errors);

  // かず数えは「数える対象」が画面に無いと問題が成立しない。
  if (type === 'count' && emoji === undefined && image === undefined) {
    errors.push(`${where}: type が count なので prompt.emoji か prompt.image が必要です`);
  }
}

/**
 * 選択肢を検証する。
 *
 * 漢字の可否は**帯によって変わる**(`allowsKanjiChoice`):
 *   - kinder 〜 e2 … 漢字は書けない(ふりがなを振る場所が無い)
 *   - e3 以降 …… 「よみ → ただしい漢字をえらぶ」が単元になるので漢字を許す。
 *     ふりがなは**付けない**(付けると答えが漏れる)ので ruby 相当の場所も持たない。
 */
function validateChoices(value: unknown, band: unknown, where: string, errors: string[]): void {
  const kanjiOk =
    (QUIZ_BANDS as readonly string[]).includes(band as string) &&
    allowsKanjiChoice(band as QuizBand);
  if (!Array.isArray(value)) {
    errors.push(`${where}: choices が配列ではありません`);
    return;
  }
  if (value.length < QUIZ_CHOICES.min || value.length > QUIZ_CHOICES.max) {
    errors.push(
      `${where}: choices は${QUIZ_CHOICES.min}〜${QUIZ_CHOICES.max}個が必要です(${value.length}個)`,
    );
  }

  let correctCount = 0;
  const seen = new Set<string>();
  for (const [i, choice] of value.entries()) {
    const at = `${where}.choices[${i}]`;
    if (!isRecord(choice)) {
      errors.push(`${at}: オブジェクトではありません`);
      continue;
    }
    const {
      text,
      textKanji,
      textKanjiSegments,
      textSegments,
      emoji,
      emojiImages,
      image,
      imageAlt,
      audio,
      correct,
    } = choice;
    if (!isText(text) && !isText(emoji) && !isText(image)) {
      errors.push(`${at}: text / emoji / image のどれか1つは必要です`);
    }
    if (text !== undefined) {
      if (!isText(text)) errors.push(`${at}: text が空です`);
      // e2 までは選択肢にふりがなを振る場所が無いので漢字を許さない。
      else if (QUIZ_KANJI.test(text) && !kanjiOk) {
        errors.push(`${at}: ${String(band)} の選択肢に漢字は使えません: ${text}`);
      }
    }
    // 漢字版の選択肢(全帯。ふりがなは `textKanjiSegments` で漢字の部分にだけ乗せる)。
    checkKanjiVariant(textKanji, text, at, 'textKanji', errors);
    checkTextSegments(textKanjiSegments, textKanji, at, 'textKanjiSegments', errors);
    // 表示用の区切り。配信JSONには載らない(`withJapaneseScript()` が入れる)ので
    // 通常は undefined だが、手書きで足されていたら壊れていないことだけ見る。
    checkTextSegments(textSegments, text, at, 'textSegments', errors);
    if (emoji !== undefined && !isText(emoji)) errors.push(`${at}: emoji が空です`);
    checkEmojiImages(emojiImages, emoji, at, 'emojiImages', errors);
    checkOptionalPath(image, at, 'image', errors);
    checkOptionalImageAlt(imageAlt, image, at, 'imageAlt', errors);
    checkOptionalPath(audio, at, 'audio', errors);
    // 読み上げるものが無い選択肢に音声だけ付いていたら、生成側の取り違え。
    if (audio !== undefined && !isText(text)) {
      errors.push(`${at}: text が無いのに audio(読み上げ)が付いています`);
    }

    // 同じ見た目の選択肢が2つあると、どちらを選んでも正解に見えてしまう。
    const key = `${String(text ?? '')} ${String(emoji ?? '')} ${String(image ?? '')}`;
    if (seen.has(key)) errors.push(`${at}: 同じ内容の選択肢が2つあります`);
    seen.add(key);

    if (correct !== undefined && typeof correct !== 'boolean') {
      errors.push(`${at}: correct は true/false が必要です`);
    }
    if (correct === true) correctCount += 1;
  }

  if (correctCount !== 1) {
    errors.push(`${where}: correct はちょうど1つ必要です(${correctCount}個)`);
  }
}

/**
 * 問題1問を検証する。
 *
 * @param value  JSON をパースしただけの値(型は信用しない)
 * @param where  エラーメッセージの位置表示(例 `quiz-kinder-kazu.json[3]`)
 */
export function validateQuizQuestion(value: unknown, where: string): string[] {
  const errors: string[] = [];
  if (!isRecord(value)) {
    errors.push(`${where}: オブジェクトではありません`);
    return errors;
  }
  const {
    id,
    subject,
    band,
    type,
    prompt,
    choices,
    explanation,
    explanationKanji,
    explanationKanjiSegments,
    explanationSegments,
    skills,
    unit,
  } = value;

  if (!isText(id)) errors.push(`${where}: id が空です`);
  else if (!QUIZ_ID_PATTERN.test(id)) errors.push(`${where}: id ${id} は英小文字+数字が必要です`);

  const knownBand = (QUIZ_BANDS as readonly string[]).includes(band as string);
  if (!knownBand) errors.push(`${where}: 未知の band ${String(band)}`);
  if (!(QUIZ_SUBJECTS as readonly string[]).includes(subject as string)) {
    errors.push(`${where}: 未知の subject ${String(subject)}`);
  } else if (
    knownBand &&
    !QUIZ_SUBJECTS_BY_BAND[band as QuizBand].includes(subject as QuizSubject)
  ) {
    errors.push(
      `${where}: ${String(band)} に教科 ${String(subject)} はありません` +
        `(有効: ${QUIZ_SUBJECTS_BY_BAND[band as QuizBand].join(' / ')})`,
    );
  }
  if (!(QUIZ_QUESTION_TYPES as readonly string[]).includes(type as string)) {
    errors.push(`${where}: 未知の type ${String(type)}`);
  }

  validatePrompt(prompt, type, band, where, errors);
  validateChoices(choices, band, where, errors);

  if (explanation !== undefined) {
    if (!isText(explanation)) errors.push(`${where}: explanation が空です`);
    else if (QUIZ_KANJI.test(explanation)) {
      errors.push(`${where}: explanation に漢字は使えません(ふりがなを振れません): ${explanation}`);
    }
  }
  // 漢字版の解説。かな版と違って漢字を書ける唯一の場所(読める子にだけ出すため)。
  checkKanjiVariant(explanationKanji, explanation, where, 'explanationKanji', errors);
  // 解説にも漢字の部分にだけ ふりがな を乗せる(ユーザー指示 2026-08-25)。
  checkTextSegments(
    explanationKanjiSegments,
    explanationKanji,
    where,
    'explanationKanjiSegments',
    errors,
  );
  checkTextSegments(explanationSegments, explanation, where, 'explanationSegments', errors);

  // **読みを問う問題**(`rubyHidden`)は問題文だけでなく選択肢・解説の漢字版も持てない
  // ―― 選択肢が「よみ」そのものなので、漢字にすると答えが並ぶことになる。
  if (isRecord(prompt) && prompt.rubyHidden === true) {
    const kanjiChoice =
      Array.isArray(choices) &&
      choices.some((choice) => isRecord(choice) && choice.textKanji !== undefined);
    if (kanjiChoice || explanationKanji !== undefined) {
      errors.push(`${where}: rubyHidden の問題に漢字版(textKanji / explanationKanji)は付けません`);
    }
  }

  if (!Array.isArray(skills) || skills.length === 0) {
    errors.push(`${where}: skills が空です(習熟タグを1つ以上)`);
  } else if (skills.some((skill) => !isText(skill))) {
    errors.push(`${where}: skills に空文字が含まれています`);
  }

  if (!isText(unit)) errors.push(`${where}: unit が空です`);
  else if (!QUIZ_ID_PATTERN.test(unit)) {
    errors.push(`${where}: unit ${unit} は英小文字+数字+ハイフンが必要です`);
  }

  return errors;
}

/**
 * 単元内ステージ(docs/13 §11)を検証する。
 *
 * ステージは**単元の問題列を順序を保ったまま切っただけ**のものなので、
 * つなぎ直したら元の並びに戻ることを見れば、
 *   - IDの重複が無い
 *   - どの問題も どこかのステージに ちょうど1回だけ入る
 *   - 並びを勝手に入れ替えていない
 * が同時に担保できる。加えて、ステージIDの形(`{単元ID}-s{番号}`)と
 * `presentation` が既知の値かを見る。
 *
 * ステージが**無い**単元は許す(ステージ制より前に配られたJSONを読み込めるように。
 * 読み取り側は `quizUnitStages()` で同じ規則を使ってその場で導出する)。
 *
 * @param questionIds 単元の問題列(検証済みでなくてよい。文字列以外は比較で落ちる)
 */
export function validateQuizStages(
  value: unknown,
  unitId: string,
  questionIds: readonly unknown[],
  where: string,
  errors: string[],
): void {
  if (value === undefined) return;
  if (!Array.isArray(value) || value.length === 0) {
    errors.push(`${where}: stages は空でない配列が必要です`);
    return;
  }

  const flattened: unknown[] = [];
  for (const [i, stage] of value.entries()) {
    const at = `${where}.stages[${i}]`;
    if (!isRecord(stage)) {
      errors.push(`${at}: オブジェクトではありません`);
      continue;
    }
    const expectedId = quizStageId(unitId, i + 1);
    if (stage.id !== expectedId) {
      errors.push(`${at}: id は ${expectedId} にしてください(${String(stage.id)})`);
    }
    if (
      stage.presentation !== undefined &&
      !(QUIZ_PRESENTATIONS as readonly string[]).includes(stage.presentation as string)
    ) {
      errors.push(
        `${at}: 未知の presentation ${String(stage.presentation)}` +
          `(有効: ${QUIZ_PRESENTATIONS.join(' / ')})`,
      );
    }
    const ids = stage.questionIds;
    if (!Array.isArray(ids) || ids.length === 0) {
      errors.push(`${at}: questionIds が空です`);
      continue;
    }
    if (ids.length < QUIZ_STAGE.minQuestions) {
      errors.push(`${at}: ステージの問題は${QUIZ_STAGE.minQuestions}問以上です(${ids.length}問)`);
    }
    flattened.push(...ids);
  }

  // つなぎ直したら元の並びに戻ること(= 抜け・重複・並べ替えが無いこと)。
  const same =
    flattened.length === questionIds.length && flattened.every((id, i) => id === questionIds[i]);
  if (!same) {
    errors.push(
      `${where}: stages をつなぐと単元の questionIds と一致しません` +
        `(ステージ計 ${flattened.length}問 / 単元 ${questionIds.length}問)`,
    );
  }
}

/**
 * 出力ファイル(`content/v{N}/quiz/{band}.json`)全体の整合を検証する。
 *
 * 1問ごとの検証に加えて、**単元と問題の相互参照**を見る:
 *  - 単元IDの重複がない / 単元の band が envelope と一致する
 *  - `unit.questionIds` がすべて解決する / 空の単元がない / 1問が2単元に入らない
 *  - 問題の `unit` が実在する単元を指し、`questionIds` と食い違わない
 *  - 単元の問題数が config の範囲に収まる
 */
export function validateQuizBank(value: unknown, label: string): string[] {
  const errors: string[] = [];
  if (!isRecord(value)) {
    errors.push(`${label}: オブジェクトではありません`);
    return errors;
  }
  if (value.schemaVersion !== QUIZ_SCHEMA_VERSION) {
    errors.push(`${label}: schemaVersion が ${QUIZ_SCHEMA_VERSION} ではありません`);
  }
  const band = value.band;
  if (!(QUIZ_BANDS as readonly string[]).includes(band as string)) {
    errors.push(`${label}: 未知の band ${String(band)}`);
  }

  const questions = value.questions;
  if (!Array.isArray(questions) || questions.length === 0) {
    errors.push(`${label}: questions が空です`);
    return errors;
  }

  const byId = new Map<string, QuizQuestion>();
  for (const [i, question] of questions.entries()) {
    errors.push(...validateQuizQuestion(question, `${label}[${i}]`));
    if (!isRecord(question) || !isText(question.id)) continue;
    if (byId.has(question.id)) errors.push(`${label}[${i}]: 問題IDが重複: ${question.id}`);
    byId.set(question.id, question as unknown as QuizQuestion);
    if (question.band !== band) {
      errors.push(`${label}[${i}]: band が ${String(band)} ではありません`);
    }
  }

  const units = value.units;
  if (!Array.isArray(units) || units.length === 0) {
    errors.push(`${label}: units が空です`);
    return errors;
  }

  const unitIds = new Set<string>();
  /** questionId → 最初にその問題を持った単元。 */
  const owner = new Map<string, string>();
  for (const [i, unit] of units.entries()) {
    const at = `${label}.units[${i}]`;
    if (!isRecord(unit)) {
      errors.push(`${at}: オブジェクトではありません`);
      continue;
    }
    if (!isText(unit.id)) {
      errors.push(`${at}: id が空です`);
      continue;
    }
    if (unitIds.has(unit.id)) errors.push(`${at}: 単元IDが重複: ${unit.id}`);
    unitIds.add(unit.id);
    if (!isText(unit.title)) errors.push(`${at}: title が空です`);
    else if (QUIZ_KANJI.test(unit.title)) errors.push(`${at}: title に漢字は使えません`);
    if (unit.band !== band) errors.push(`${at}: band が ${String(band)} ではありません`);

    // 単元冒頭の解説パート(docs/13 §8)。**持っている単元だけ**が検証の対象。
    // `lesson.id` を単元IDに揃えるのは、管理画面・原本ファイルから1対1で辿れるようにするため。
    if (unit.lesson !== undefined) {
      errors.push(...validateQuizLesson(unit.lesson, `${at}.lesson`, knownBandOf(band)));
      if (isRecord(unit.lesson) && isText(unit.lesson.id) && unit.lesson.id !== unit.id) {
        errors.push(`${at}: lesson.id は単元IDと同じにしてください(${unit.lesson.id})`);
      }
    }

    const questionIds = unit.questionIds;
    if (!Array.isArray(questionIds) || questionIds.length === 0) {
      errors.push(`${at}: ${unit.id} に問題がありません`);
      continue;
    }
    if (
      questionIds.length < QUIZ_UNIT.minQuestions ||
      questionIds.length > QUIZ_UNIT.maxQuestions
    ) {
      errors.push(
        `${at}: ${unit.id} の問題数は${QUIZ_UNIT.minQuestions}〜${QUIZ_UNIT.maxQuestions}問です` +
          `(${questionIds.length}問)`,
      );
    }
    // 単元内ステージ(docs/13 §11)。無い単元も許す(古い配信JSONの読み込み)。
    validateQuizStages(unit.stages, unit.id, questionIds, at, errors);

    for (const questionId of questionIds) {
      const question = typeof questionId === 'string' ? byId.get(questionId) : undefined;
      if (!question) {
        errors.push(`${at}: 問題 ${String(questionId)} が解決できません`);
        continue;
      }
      const previous = owner.get(question.id);
      if (previous !== undefined) {
        errors.push(`${at}: 問題 ${question.id} が ${previous} と ${unit.id} の両方に入っています`);
      } else {
        owner.set(question.id, unit.id);
      }
      if (question.unit !== unit.id) {
        errors.push(`${at}: 問題 ${question.id} の unit は ${question.unit} です`);
      }
      if (question.subject !== unit.subject) {
        errors.push(`${at}: 問題 ${question.id} の subject が単元と違います`);
      }
    }
  }

  for (const question of byId.values()) {
    if (!unitIds.has(question.unit)) {
      errors.push(
        `${label}: ${question.id} の unit ${question.unit} が単元として定義されていません`,
      );
    } else if (!owner.has(question.id)) {
      errors.push(`${label}: ${question.id} がどの単元の questionIds にも入っていません`);
    }
  }

  return errors;
}
